import { useEffect, useRef, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';

// State
import { getIsAllConditionsCheckedSelector } from '../../../state/modules/stock/selectors';
import { removeCartItem, addCartItem } from '../../../state/modules/cart/actions';
import {
  applyStock,
  addReward,
  removeIngredient,
  cancelRemovedIngredients,
} from '../../../state/modules/stock/actions';

// Utils
import { customScrollTo } from '../../../utils/customScrollTo';

// Router
import { paths, getRoute } from '../../../entry/routes';

export default function StockGoods({ stockRole }) {
  const params = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const allConditionsChecked = useSelector(getIsAllConditionsCheckedSelector);
  const isApplied = useSelector(state => state.stock.isApplied);
  const data = useSelector(state => state.stock.data);

  // КАТАЛОГ С ТОВАРАМИ (полные объекты)
  const catalogGoods = useSelector(state => state.catalog.products.list) || [];

  const stockGoodsRef = useRef({});
  const [isAdded, setIsAdded] = useState(false);

  const { code = '', details = {} } = data || {};
  const { conditions = [], rewards = [] } = details || {};
  const { stockCode, conditionIdx, rewardIdx } = params;

  // Текущие узлы
  const currentCondition = conditions[Number(conditionIdx)] || {};
  const currentReward = rewards[Number(rewardIdx)] || {};

  const isConditions = stockRole === 'conditions';
  const type = isConditions ? 'good' : 'gift';
  const title = isConditions ? currentCondition.criteria || '' : currentReward.message || '';

  // --- НОВОЕ: гидрация "лёгких" ссылок на товары в полные объекты из каталога ---

  // Быстрый индекс по товарам каталога
  const goodsById = useMemo(() => {
    const map = new Map();
    for (const g of catalogGoods) map.set(Number(g.id), g);
    return map;
  }, [catalogGoods]);

  // helper: определить «старая» это схема (полные товары) или «новая» (ссылки)
  const isFullGoodObject = good =>
    good &&
    typeof good === 'object' &&
    ('name' in good || (Array.isArray(good.variations) && typeof good.variations[0] === 'object'));

  // Гидрация списка good’ов (работает и для conditions, и для rewards)
  const hydratedGoods = useMemo(() => {
    const raw = isConditions ? currentCondition.goods || [] : currentReward.goods || [];
    if (!Array.isArray(raw) || raw.length === 0) return [];

    // Если пришла старая схема — просто вернуть как есть
    if (isFullGoodObject(raw[0])) return raw;

    // Новая схема: [{ good_id, variations: [id, id] }, ...]
    return raw
      .map(link => {
        const good = goodsById.get(Number(link.good_id));
        if (!good) return null;

        // оставим только указанные вариации (если variations пуст — вернем все)
        const requestedIds = Array.isArray(link.variations)
          ? new Set(link.variations.map(Number))
          : null;

        const filteredVariations = Array.isArray(good.variations)
          ? good.variations.filter(v => (requestedIds ? requestedIds.has(Number(v.id)) : true))
          : [];

        if (filteredVariations.length === 0) return null;

        // Вернём «клон» товара, но только с нужными вариациями
        return {
          ...good,
          variations: filteredVariations,
        };
      })
      .filter(Boolean);
  }, [isConditions, currentCondition.goods, currentReward.goods, goodsById]);

  // не выводим товары, которые есть в стоп-листе
  const filtredGoods = useMemo(() => {
    return hydratedGoods.filter(
      good =>
        Array.isArray(good.variations) &&
        good.variations.some(variation => !variation.is_in_stop_list),
    );
  }, [hydratedGoods]);

  // --- actions остаются прежними ---

  const productIndex = isConditions ? conditionIdx : rewardIdx;

  const removeIngredientAction = ({ id, variation, ingredientId }) => {
    return dispatch(removeIngredient(id, variation.id, productIndex, ingredientId, stockRole));
  };

  const cancelRemovedIngredientsAction = ({ id, variation }) => {
    return dispatch(cancelRemovedIngredients(id, variation.id, productIndex, stockRole));
  };

  const addToCart = async variation => {
    const { replace, item } = qs.parse(location.search);

    if (isConditions) {
      if (replace && item) {
        dispatch(removeCartItem(item));
      }
      await dispatch(addCartItem({ composition: [{ type: 'good', item: variation }] }));
      setIsAdded(true);
      return;
    }

    const ingredients = variation.include_ingredients || [];
    const filtredIngredients =
      ingredients
        .filter(ingredient => ingredient.removed)
        .map(ingredient => ({ id: ingredient.id, count: 0 })) || [];

    const composition = [{ id: variation.id, ingredients: filtredIngredients }];

    await dispatch(addReward(rewardIdx, composition, replace !== undefined));
    setIsAdded(true);
  };

  // Скролл + автоприменение промокода из урла
  useEffect(() => {
    customScrollTo(stockGoodsRef.current, { block: 'start' });
    if (stockCode !== code) {
      dispatch(applyStock({ stock_code: stockCode }));
    }
    return () => {
      customScrollTo(stockGoodsRef.current, { block: 'start' });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // если все условия выполнены
  useEffect(() => {
    if (isAdded && (isConditions ? currentCondition.check : currentReward.applied)) {
      history.goBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdded, isConditions, currentCondition.check, currentReward.applied]);

  // если промокод не применен — уход на главную
  useEffect(() => {
    if (!isApplied) {
      history.push(getRoute(paths.home));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApplied]);

  // если это призы, но условия не выполнены — уход на главную
  useEffect(() => {
    if (stockRole === 'rewards' && !allConditionsChecked) {
      history.push(getRoute(paths.home));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockRole, allConditionsChecked]);

  return {
    stockGoodsRef,
    title,
    filtredGoods, // уже «полные» товары, обрезанные по variations и стоп-листу
    removeIngredientAction,
    cancelRemovedIngredientsAction,
    addToCart,
    type,
  };
}
